/* eslint-disable max-lines -- Pre-existing violation */
import {
  Alert,
  Button,
  ConfirmDialog,
  DataStatus,
  Dialog,
  Form,
  showNotification,
  type BlockerContext,
  type FormControl,
  type NavigateWithoutBlocker,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useWatch, type FieldErrors, type UseFormReturn} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {partition} from 'ramda';

import {selectActiveBranchId} from '@dms/api/features';
import {
  type CatalogueTreeResponseBody,
  type ConfigurationDetailResponseBody,
  type ConfigurationSelection,
  type EvaluateConfigurationResponseBody,
  isDefiniteNewVehicleRejection,
  isNewVehicleConflict,
  useCopyConfigurationMutation,
  useCreateConfigurationMutation,
  useGetAuthorConfiguratorBundleQuery,
  useGetCatalogueTreeQuery,
  useGetConfiguratorBundleQuery,
  useLazyGetConfigurationDetailQuery,
  useRepriceConfigurationMutation,
  useUpdateConfigurationMutation,
} from '@dms/api/new-vehicle';
import {getApiErrorMessages} from '@dms/api/utils';
import {
  getNewVehicleEnumColorScheme,
  getNewVehicleEnumLabel,
  getTrimOrderability,
  NewVehiclePageHeader,
} from '@dms/features/new-vehicle';
import {useWizardTabRouting} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, refreshDmsDatagrid, usePermissions} from '@dms/shared';

import {
  getApiDateString,
  getSafely,
  parseDate,
  type ApiError,
  useNavigate,
  useOnMount,
} from 'shared/web';

import {NEW_VEHICLE_CONTENT_MAX_WIDTH} from '../NewVehicleShell/NEW_VEHICLE_CONTENT_MAX_WIDTH';
import {ConfiguratorLayout} from './components/ConfiguratorLayout';
import {ConflictReview as ConflictReviewContent} from './components/ConflictReview';
import {EngineDrive} from './components/sections/EngineDrive';
import {LookAndSummary} from './components/sections/LookAndSummary';
import {ModelYearTrim} from './components/sections/ModelYearTrim';
import {OptionsPackages} from './components/sections/OptionsPackages';
import {Review} from './components/sections/Review';
import {useEvaluateSelection} from './hooks/useEvaluateSelection';
import {configuratorFormSchema} from './utils/configuratorFormSchema';
import {getOrderNowActionState} from './utils/getOrderNowActionState';
import {getReviewPriceSource} from './utils/getReviewPriceSource';
import {getSelectionContext} from './utils/getSelectionContext';
import {normalizeUntouchedDiscount} from './utils/normalizeUntouchedDiscount';
import {
  getPendingCreatedConfigurationKey,
  pendingCreatedConfigurationStore,
} from './utils/pendingCreatedConfigurationStore';
import {isCompleteProposedDiscount} from './utils/proposedDiscount';

export type ConfiguratorFormValues = z.infer<typeof configuratorFormSchema>;

type Props = {
  detail?: ConfigurationDetailResponseBody;
  draftKey: string;
  initialDraft?: ConfiguratorFormValues;
  deepLink?: {modelYearId?: string; trimId?: string};
  businessCaseId?: string;
  replaceSaleVehicleId?: string;
  interestId?: string;
  mode?: 'configure' | 'preview';
  previewContext?: {
    makeId: string;
    makeName: string;
    modelId: string;
    modelName: string;
    modelYearName: string;
    status: string;
    version: number;
  };
  onOrderConfiguration?: (configurationId: string, isIntentCurrent: () => boolean) => void;
  onRegisterExternalOrder?: (configurationId: string, isIntentCurrent: () => boolean) => void;
};
type ConflictReview = {
  before: EvaluateConfigurationResponseBody;
  after: EvaluateConfigurationResponseBody;
  values: ConfiguratorFormValues;
  intentKey: string;
  version?: number;
};
type PendingAction = 'save' | 'copy' | 'reprice';
type ConfiguratorSubmitBridge = {
  submit: (values: ConfiguratorFormValues, blockerContext?: BlockerContext) => Promise<void>;
  invalidSubmit: (errors: FieldErrors<ConfiguratorFormValues>) => void;
};
const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});
const tValues = (key: string, options: {defaultValue: string; [value: string]: string}) =>
  i18n.t(key, options);
const STEP_INDEX: Record<string, number> = {'1': 0, '2': 1, '3': 2, '4': 3, '5': 4};
const toSelection = (values: ConfiguratorFormValues): ConfigurationSelection => ({
  modelYearId: values.modelYearId,
  trimId: values.trimId,
  variantId: values.variantId,
  colourId: values.colourId || null,
  upholsteryId: values.upholsteryId || null,
  featureIds: values.featureIds,
  packageIds: values.packageIds,
});
// CF-0010 settlement: the saved selection must echo every auto-added id explicitly (features and packages).
const toSettledSelection = (
  values: ConfiguratorFormValues,
  evaluation: EvaluateConfigurationResponseBody
): ConfigurationSelection => {
  const selection = toSelection(values);
  const packageIds = new Set(
    evaluation.items.flatMap((item) => (item.type === 'package' ? [item.id] : []))
  );
  const [autoAddedPackages, autoAddedFeatures] = partition(
    (id) => packageIds.has(id),
    evaluation.autoAddedSelectionIds
  );
  return {
    ...selection,
    featureIds: [...new Set([...selection.featureIds, ...autoAddedFeatures])],
    packageIds: [...new Set([...selection.packageIds, ...autoAddedPackages])],
  };
};
const getDraftIntentKey = (values: ConfiguratorFormValues) =>
  JSON.stringify({
    label: values.label,
    selection: toSelection(values),
    proposedDiscount: normalizeUntouchedDiscount(values.proposedDiscount) ?? null,
  });
// What a draft is worth resuming for: make and model count even before a model year is picked.
const getDraftContentKey = (values: ConfiguratorFormValues) =>
  JSON.stringify([values.makeId, values.modelId, getDraftIntentKey(values)]);
const getSaveIntentKey = (values: ConfiguratorFormValues) =>
  JSON.stringify({
    label: values.label.trim(),
    selection: toSelection(values),
    proposedDiscount: normalizeUntouchedDiscount(values.proposedDiscount) ?? null,
  });
const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const hasApiErrorCode = (error: unknown, code: string) =>
  isRecord(error) &&
  isRecord(error.data) &&
  Array.isArray(error.data.errors) &&
  error.data.errors.some((item) => isRecord(item) && item.code === code);
const isClientRejection = (error: unknown): error is ApiError =>
  isRecord(error) &&
  typeof error.status === 'number' &&
  error.status === 400 &&
  isRecord(error.data) &&
  Array.isArray(error.data.errors) &&
  isRecord(error.data.errors[0]) &&
  typeof error.data.errors[0].code === 'string';
const pendingCreateNotifications = new Set<string>();

function AdjustmentGuard(props: {
  evaluation?: EvaluateConfigurationResponseBody;
  form: UseFormReturn<ConfiguratorFormValues>;
  names: Map<string, string>;
}) {
  const appliedResultKey = useRef('');
  const invalidatedIds = props.evaluation?.invalidatedSelectionIds.join(',') ?? '';
  const resultKey = props.evaluation
    ? `${props.evaluation.catalogueRevision}:${invalidatedIds}`
    : '';

  // Each evaluator result remounts this guard, so reconciliation runs once at the query boundary.
  useOnMount(() => {
    if (!props.evaluation || !invalidatedIds || appliedResultKey.current === resultKey) {
      return;
    }
    const dropped = props.evaluation.invalidatedSelectionIds;
    const values = props.form.getValues();
    props.form.setValue(
      'featureIds',
      values.featureIds.filter((id) => !dropped.includes(id)),
      {shouldDirty: true}
    );
    props.form.setValue(
      'packageIds',
      values.packageIds.filter((id) => !dropped.includes(id)),
      {shouldDirty: true}
    );
    if (values.colourId && dropped.includes(values.colourId)) {
      props.form.setValue('colourId', '', {shouldDirty: true});
    }
    if (values.upholsteryId && dropped.includes(values.upholsteryId)) {
      props.form.setValue('upholsteryId', '', {shouldDirty: true});
    }
    appliedResultKey.current = resultKey;
    showNotification.warning(
      `${t('entity.newVehicle.notifications.selectionsDropped', 'Selections removed')}: ${dropped.map((id) => props.names.get(id) ?? t('entity.newVehicle.labels.unknownSelection', 'Unavailable selection')).join(', ')}`
    );
  });
  if (!props.evaluation?.invalidatedSelectionIds.length) {
    return null;
  }
  return (
    <Alert
      variant="warning"
      message={`${t('entity.newVehicle.notifications.selectionsDropped', 'Selections removed')}: ${props.evaluation.invalidatedSelectionIds
        .map(
          (id) =>
            props.names.get(id) ??
            t('entity.newVehicle.labels.unknownSelection', 'Unavailable selection')
        )
        .join(', ')}`}
    />
  );
}

const ConfiguratorBody = forwardRef<
  ConfiguratorSubmitBridge,
  {
    control: FormControl<ConfiguratorFormValues>;
    form: UseFormReturn<ConfiguratorFormValues>;
    tree?: CatalogueTreeResponseBody;
    isTreeLoading: boolean;
    isTreeError: boolean;
    canViewCost: boolean;
    canEditCustomFields: boolean;
    canOrder: boolean;
    canSelect: boolean;
    canWrite: boolean;
    detail?: ConfigurationDetailResponseBody;
    draftKey: string;
    draftStartKey: string;
    businessCaseId?: string;
    replaceSaleVehicleId?: string;
    interestId?: string;
    mode: 'configure' | 'preview';
    navigateWithoutBlocker: NavigateWithoutBlocker;
    previewContext?: Props['previewContext'];
    onOrderConfiguration?: Props['onOrderConfiguration'];
    onRegisterExternalOrder?: Props['onRegisterExternalOrder'];
  }
  // eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
>(function ConfiguratorBody(props, ref) {
  const navigate = useNavigate();
  const activeBranchId = useSelector(selectActiveBranchId);
  useEffect(
    function persistDirtyDraftChanges() {
      if (props.mode === 'preview' || props.detail) {
        return;
      }
      return props.form.subscribe({
        formState: {values: true, isDirty: true},
        callback: ({name, values}) => {
          if (!name) {
            return;
          }
          // reset() emits named field events too, so the mount itself looks like input. Only a
          // selection that differs from where this configurator started is worth resuming;
          // anything else, including a user undoing every choice, leaves no draft behind.
          if (getDraftContentKey(values) === props.draftStartKey) {
            sessionStorage.removeItem(props.draftKey);
          } else {
            sessionStorage.setItem(props.draftKey, JSON.stringify(values));
          }
        },
      });
    },
    [props.detail, props.draftKey, props.draftStartKey, props.form, props.mode]
  );
  const formatDate = useDateTimeFormatter();
  const specFreezeDate = props.detail?.order?.specFreezeDate;
  const isFactoryFrozen = Boolean(specFreezeDate && specFreezeDate < getApiDateString(new Date()));
  const factoryFreezeMessage = specFreezeDate
    ? tValues('entity.newVehicle.notifications.factorySpecificationFrozen', {
        defaultValue:
          'Factory specification frozen since {date} — only dealer-fit accessories, label and custom fields can be changed',
        date: formatDate('dateMedium', parseDate(specFreezeDate)),
      })
    : undefined;
  const factoryFreezeReason = isFactoryFrozen ? factoryFreezeMessage : undefined;
  const modelYearId = useWatch({control: props.control, name: 'modelYearId'});
  const trimId = useWatch({control: props.control, name: 'trimId'});
  const variantId = useWatch({control: props.control, name: 'variantId'});
  const colourId = useWatch({control: props.control, name: 'colourId'});
  const upholsteryId = useWatch({control: props.control, name: 'upholsteryId'});
  const featureIds = useWatch({control: props.control, name: 'featureIds'});
  const packageIds = useWatch({control: props.control, name: 'packageIds'});
  const label = useWatch({control: props.control, name: 'label'});
  const proposedDiscountKind = useWatch({
    control: props.control,
    name: 'proposedDiscount.kind',
  });
  const proposedDiscountValue = useWatch({
    control: props.control,
    name: 'proposedDiscount.value',
  });
  const proposedDiscountBasis = useWatch({
    control: props.control,
    name: 'proposedDiscount.basis',
  });
  const proposedDiscount =
    proposedDiscountKind &&
    proposedDiscountBasis &&
    proposedDiscountValue &&
    isCompleteProposedDiscount({
      kind: proposedDiscountKind,
      value: proposedDiscountValue,
      basis: proposedDiscountBasis,
    })
      ? {
          kind: proposedDiscountKind,
          value: proposedDiscountValue,
          basis: proposedDiscountBasis,
        }
      : undefined;
  const rawDiscountIntent = normalizeUntouchedDiscount({
    kind: proposedDiscountKind,
    value: proposedDiscountValue,
    basis: proposedDiscountBasis,
  });
  const hasIncompleteDiscountIntent = rawDiscountIntent !== null && !proposedDiscount;
  const currentSelection = {
    modelYearId,
    trimId,
    variantId,
    colourId: colourId || null,
    upholsteryId: upholsteryId || null,
    featureIds,
    packageIds,
  };
  const priceSource = getReviewPriceSource(
    props.detail?.configuration.selection,
    props.detail?.currentPriceSnapshot.proposedDiscount,
    currentSelection,
    proposedDiscount
  );
  // URL owns the wizard step; the ref is unused because the stepper is URL-driven.
  const {onStepChange, currentTab} = useWizardTabRouting({
    tabStepMap: STEP_INDEX,
    tabRoutePattern:
      props.mode === 'preview'
        ? vehiclesRoutes.newVehicleCatalogueModelYearPreview
        : vehiclesRoutes.newVehicleConfigurator,
    pathParamKey: 'step',
    defaultTab: '1',
  });
  const isUnchangedSavedReview =
    props.mode === 'configure' &&
    currentTab === '5' &&
    priceSource === 'saved' &&
    label === props.detail?.configuration.label;
  const orderConfigurationId = props.detail?.configuration.id;
  const externalRegistrationConfigurationId = props.detail?.configuration.id;
  const externalRegistrationBlocker =
    props.detail?.order || props.detail?.vehicle // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? t('entity.newVehicle.order.alreadyOrdered', 'This configuration is already ordered')
      : hasIncompleteDiscountIntent || !isUnchangedSavedReview
        ? t(
            'entity.newVehicle.external.saveChangesFirst',
            'Save configuration changes before registering an external order'
          )
        : undefined;
  const requestedModelYearId =
    modelYearId || props.detail?.configuration.selection.modelYearId || '';
  const configuredBundleQuery = useGetConfiguratorBundleQuery(
    {modelYearId: requestedModelYearId},
    {skip: !requestedModelYearId || props.mode === 'preview'}
  );
  const previewBundleQuery = useGetAuthorConfiguratorBundleQuery(
    {modelYearId: requestedModelYearId},
    {skip: !requestedModelYearId || props.mode !== 'preview'}
  );
  const bundle =
    props.mode === 'preview' ? previewBundleQuery.currentData : configuredBundleQuery.currentData;
  const basis = bundle?.priceBasis;
  const isBundleError =
    props.mode === 'preview' ? previewBundleQuery.isError : configuredBundleQuery.isError;
  const evaluation = useEvaluateSelection(
    currentSelection,
    bundle?.catalogueRevision,
    proposedDiscount,
    props.mode,
    !isUnchangedSavedReview,
    props.previewContext?.version
  );
  const [createConfiguration, createState] = useCreateConfigurationMutation();
  const [updateConfiguration, updateState] = useUpdateConfigurationMutation();
  const [copyConfiguration, copyState] = useCopyConfigurationMutation();
  const [repriceConfiguration, repriceState] = useRepriceConfigurationMutation();
  const [getConfigurationDetail] = useLazyGetConfigurationDetailQuery();
  const [conflict, setConflict] = useState<ConflictReview>();
  const [repriceConflict, setRepriceConflict] = useState(false);
  const [copiedConfigurationId, setCopiedConfigurationId] = useState<string>();
  const [isCopyOpenConfirmationOpen, setIsCopyOpenConfirmationOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [isRecoveryPending, setIsRecoveryPending] = useState(false);
  const operationLock = useRef(false);
  const recoveryPending = useRef(false);
  const recoverySequence = useRef(0);
  const createOwnerId = useRef(crypto.randomUUID());
  const pendingCreatedConfigurationKey = getPendingCreatedConfigurationKey(
    props.draftKey,
    props.businessCaseId,
    props.interestId
  );
  const isMounted = useRef(true);
  useOnMount(
    () => {
      isMounted.current = true;
    },
    () => {
      isMounted.current = false;
      recoverySequence.current += 1;
    }
  );
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const saveGateStatus = match({
    isRecoveryPending,
    pendingAction,
    isEvaluationPending: evaluation.isEligible && !evaluation.isSettled && !evaluation.error,
  })
    .with({isRecoveryPending: true}, () =>
      t('entity.newVehicle.actions.recoveringSave', 'Refreshing after a save conflict…')
    )
    .with({pendingAction: Pattern.not(undefined)}, () =>
      t('entity.newVehicle.actions.pendingAction', 'Another action is in progress…')
    )
    .with({isEvaluationPending: true}, () =>
      t('entity.newVehicle.actions.evaluatingSave', 'Evaluating configuration before save…')
    )
    .otherwise(() => undefined);
  const persistentOrderNowAction = getOrderNowActionState(
    bundle?.trims.find((trim) => trim.id === trimId),
    new Date(),
    (date) => formatDate('dateMedium', date),
    props.canOrder
  );
  const showSavedNotification = (configurationId: string, isIntentCurrent: () => boolean) =>
    showNotification.success(
      t('entity.newVehicle.notifications.configurationSaved', 'Configuration saved'),
      {
        layout: 'detailed',
        actions: [
          {
            title: persistentOrderNowAction.title,
            isDisabled: persistentOrderNowAction.isDisabled,
            onClick: () => {
              if (props.canOrder) {
                props.onOrderConfiguration?.(
                  configurationId,
                  () => isMounted.current && isIntentCurrent()
                );
              }
            },
          },
          {
            title: t('entity.newVehicle.actions.openList', 'Open in list'),
            onClick: () => navigate(vehiclesRoutes.newVehicleConfigurations),
          },
        ],
      }
    );
  useOnMount(() => {
    const configurationId = props.detail?.configuration.id;
    if (!configurationId || !pendingCreateNotifications.delete(configurationId)) {
      return;
    }
    const notificationIntentKey = getDraftIntentKey(props.form.getValues());
    showSavedNotification(
      configurationId,
      () => getDraftIntentKey(props.form.getValues()) === notificationIntentKey
    );
  });
  const beginAction = (action: PendingAction) => {
    if (operationLock.current || recoveryPending.current) {
      return false;
    }
    recoverySequence.current += 1;
    operationLock.current = true;
    setPendingAction(action);
    return true;
  };
  const endAction = () => {
    operationLock.current = false;
    if (isMounted.current) {
      setPendingAction(undefined);
    }
  };
  const currentStepIndex = getSafely(STEP_INDEX, currentTab ?? '1') ?? 0;
  const names = new Map(
    [
      ...(bundle?.features ?? []),
      ...(bundle?.packages ?? []),
      ...(bundle?.colours ?? []),
      ...(bundle?.upholsteries ?? []),
    ].map(({id, name}) => [id, name])
  );
  const contextTree =
    props.tree ??
    (props.previewContext
      ? {
          makes: [
            {
              id: props.previewContext.makeId,
              name: props.previewContext.makeName,
              models: [
                {
                  id: props.previewContext.modelId,
                  name: props.previewContext.modelName,
                  modelYears: [{id: modelYearId, name: props.previewContext.modelYearName}],
                },
              ],
            },
          ],
        }
      : undefined);
  const selectionContext = getSelectionContext(contextTree, bundle, {
    modelYearId,
    trimId,
    variantId,
    colourId,
    upholsteryId,
  });
  const reviewContext = {
    makeName: selectionContext.make?.name ?? '',
    modelName: selectionContext.model?.name ?? '',
    modelYearName: selectionContext.modelYear?.name ?? '',
  };
  const hasSelectableTrim = Boolean(
    trimId &&
      bundle?.trims.some(
        (trim) =>
          trim.id === trimId &&
          (props.mode === 'preview' || getTrimOrderability(trim, new Date()).orderable)
      )
  );

  // eslint-disable-next-line max-params, max-statements -- Pre-existing violation
  const performSave = async (
    values: ConfiguratorFormValues,
    revision: string,
    beforeEvaluation: EvaluateConfigurationResponseBody,
    version = props.detail?.configuration.version,
    shouldNavigate = true,
    rawIntentKey = getDraftIntentKey(props.form.getValues())
  ): Promise<boolean> => {
    try {
      if (
        !props.detail &&
        !pendingCreatedConfigurationStore.claim(pendingCreatedConfigurationKey, {
          ownerId: createOwnerId.current,
          intentKey: rawIntentKey,
        })
      ) {
        showNotification.error(
          t(
            'entity.newVehicle.notifications.saveRecoveryUnavailable',
            'The configuration could not be saved safely. Free browser storage and try again.'
          )
        );
        return false;
      }
      const selection = toSettledSelection(values, beforeEvaluation);
      const id = props.detail
        ? props.detail.configuration.id
        : (
            await createConfiguration({
              'X-Branch': activeBranchId,
              createConfigurationRequestBody: {
                label: values.label,
                selection,
                catalogueRevision: revision,
                businessCaseId: props.businessCaseId ?? null,
                interestId: props.interestId ?? null,
                proposedDiscount: normalizeUntouchedDiscount(values.proposedDiscount) ?? null,
              },
            }).unwrap()
          ).id;
      if (!props.detail) {
        const completion = pendingCreatedConfigurationStore.complete(
          pendingCreatedConfigurationKey,
          createOwnerId.current,
          rawIntentKey,
          id
        );
        if (completion === 'memory-only') {
          showNotification.warning(
            t(
              'entity.newVehicle.notifications.saveRecoveryNotPersisted',
              'Configuration saved, but recovery is available only until this page is closed.'
            )
          );
        }
      }
      if (props.detail) {
        await updateConfiguration({
          configurationId: props.detail.configuration.id,
          updateConfigurationRequestBody: {
            label: values.label,
            selection,
            catalogueRevision: revision,
            version: version ?? props.detail.configuration.version,
            proposedDiscount: normalizeUntouchedDiscount(values.proposedDiscount) ?? null,
          },
        }).unwrap();
      }
      if (!isMounted.current || getDraftIntentKey(props.form.getValues()) !== rawIntentKey) {
        if (!props.detail && isMounted.current) {
          setCopiedConfigurationId(id);
        }
        return false;
      }
      if (!props.detail) {
        pendingCreatedConfigurationStore.clearOwned(
          pendingCreatedConfigurationKey,
          createOwnerId.current
        );
      }
      recoverySequence.current += 1;
      refreshDmsDatagrid('new-vehicle-configuration');
      // Reset before clearing the draft: reset() emits field events the draft subscription persists.
      props.form.reset({
        ...values,
        featureIds: selection.featureIds,
        packageIds: selection.packageIds,
      });
      const notificationIntentKey = getDraftIntentKey(props.form.getValues());
      sessionStorage.removeItem(props.draftKey);
      setConflict(undefined);
      if (!props.detail && shouldNavigate) {
        pendingCreateNotifications.add(id);
      } else {
        showSavedNotification(
          id,
          () => getDraftIntentKey(props.form.getValues()) === notificationIntentKey
        );
      }
      if (shouldNavigate) {
        props.navigateWithoutBlocker(vehiclesRoutes.newVehicleConfigurator, {
          params: {step: '5'},
          queryParams: {
            configurationId: id,
            businessCaseId: props.businessCaseId,
            replaceSaleVehicleId: props.replaceSaleVehicleId,
            interestId: props.interestId,
          },
        });
      }
      return true;
    } catch (error) {
      if (!props.detail) {
        if (isDefiniteNewVehicleRejection(error)) {
          pendingCreatedConfigurationStore.fail(
            pendingCreatedConfigurationKey,
            createOwnerId.current
          );
        } else {
          pendingCreatedConfigurationStore.markUncertain(
            pendingCreatedConfigurationKey,
            createOwnerId.current
          );
        }
      }
      if (hasApiErrorCode(error, 'DMS-NV-CF-0004') && factoryFreezeMessage) {
        showNotification.warning(factoryFreezeMessage);
      } else if (isNewVehicleConflict(error)) {
        if (!isMounted.current) {
          return false;
        }
        const intentKey = getSaveIntentKey(values);
        const sequence = recoverySequence.current + 1;
        recoverySequence.current = sequence;
        recoveryPending.current = true;
        setIsRecoveryPending(true);
        const refreshedDetail = props.detail
          ? getConfigurationDetail({configurationId: props.detail.configuration.id}, false).unwrap()
          : Promise.resolve(undefined);
        void Promise.all([
          configuredBundleQuery.refetch().unwrap(),
          evaluation.refetch().unwrap(),
          refreshedDetail,
        ])
          .then(([, refreshedEvaluation, latestDetail]) => {
            if (
              !isMounted.current ||
              recoverySequence.current !== sequence ||
              getSaveIntentKey(props.form.getValues()) !== intentKey
            ) {
              if (isMounted.current && recoverySequence.current === sequence) {
                showNotification.warning(
                  t(
                    'entity.newVehicle.order.conflict',
                    'This configuration changed. Review the refreshed details and try again.'
                  )
                );
              }
              return;
            }
            setConflict({
              before: beforeEvaluation,
              after: refreshedEvaluation,
              values,
              intentKey,
              version: latestDetail?.configuration.version,
            });
          })
          .catch((recoveryError: unknown) => {
            if (!isMounted.current || recoverySequence.current !== sequence) {
              return;
            }
            if (isApiError(recoveryError)) {
              handleApiError(recoveryError);
            } else {
              showNotification.error(
                t('general.labels.somethingWentWrong', 'Something went wrong.')
              );
            }
          })
          .finally(() => {
            if (!isMounted.current || recoverySequence.current !== sequence) {
              return;
            }
            recoveryPending.current = false;
            setIsRecoveryPending(false);
          });
      } else if (isApiError(error)) {
        handleApiError(error);
      } else {
        showNotification.error(t('general.labels.somethingWentWrong', 'Something went wrong.'));
      }
      return false;
    }
  };
  const save = async (values: ConfiguratorFormValues, blockerContext?: BlockerContext) => {
    const currentValues = props.form.getValues();
    if (
      props.mode === 'preview' ||
      !props.canWrite ||
      conflict ||
      (!props.detail && Boolean(copiedConfigurationId)) ||
      getSaveIntentKey(values) !== getSaveIntentKey(currentValues) ||
      !evaluation.currentData ||
      !evaluation.isSettled ||
      !beginAction('save')
    ) {
      blockerContext?.reportApiFailure?.();
      return;
    }
    const rawIntentKey = getDraftIntentKey(currentValues);
    try {
      const didSave = await performSave(
        values,
        evaluation.currentData.catalogueRevision,
        evaluation.currentData,
        undefined,
        !blockerContext?.wasSubmittedFromBlocker,
        rawIntentKey
      );
      if (!didSave) {
        blockerContext?.reportApiFailure?.();
      }
    } catch (error) {
      blockerContext?.reportApiFailure?.();
      if (isApiError(error)) {
        handleApiError(error);
      } else {
        showNotification.error(t('general.labels.somethingWentWrong', 'Something went wrong.'));
      }
    } finally {
      endAction();
    }
  };
  const invalidSubmit = (errors: FieldErrors<ConfiguratorFormValues>) => {
    if (!errors.proposedDiscount) {
      return;
    }
    showNotification.error(
      t(
        'entity.newVehicle.notifications.completeProposedDiscount',
        'Complete all proposed discount fields'
      )
    );
    onStepChange(3);
  };
  useImperativeHandle(ref, () => ({submit: save, invalidSubmit}));

  const isConflictIntentCurrent = conflict
    ? conflict.intentKey === getSaveIntentKey(props.form.getValues())
    : false;
  const copyBlocker = hasIncompleteDiscountIntent // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? t(
        'entity.newVehicle.notifications.completeProposedDiscount',
        'Complete all proposed discount fields'
      )
    : priceSource === 'live'
      ? t(
          'entity.newVehicle.order.conflict',
          'This configuration changed. Review the refreshed details and try again.'
        )
      : undefined;
  const copySavedConfiguration = () => {
    if (!props.detail || copyBlocker || !beginAction('copy')) {
      return;
    }
    const intentKey = getDraftIntentKey(props.form.getValues());
    void copyConfiguration({
      configurationId: props.detail.configuration.id,
      copyConfigurationRequestBody: {label: props.form.getValues('label')},
    })
      .unwrap()
      .then(({id}) => {
        if (!isMounted.current) {
          return;
        }
        if (getDraftIntentKey(props.form.getValues()) !== intentKey) {
          setCopiedConfigurationId(id);
          return;
        }
        props.navigateWithoutBlocker(vehiclesRoutes.newVehicleConfigurator, {
          params: {step: '5'},
          queryParams: {
            configurationId: id,
            businessCaseId: props.businessCaseId,
            replaceSaleVehicleId: props.replaceSaleVehicleId,
            interestId: props.interestId,
          },
        });
      })
      .catch((error: unknown) => {
        if (isMounted.current && isApiError(error)) {
          handleApiError(error);
        }
      })
      .finally(endAction);
  };
  const openCopiedConfiguration = () => {
    if (!copiedConfigurationId) {
      return;
    }
    setIsCopyOpenConfirmationOpen(false);
    props.navigateWithoutBlocker(vehiclesRoutes.newVehicleConfigurator, {
      params: {step: '5'},
      queryParams: {
        configurationId: copiedConfigurationId,
        businessCaseId: props.businessCaseId,
        replaceSaleVehicleId: props.replaceSaleVehicleId,
        interestId: props.interestId,
      },
    });
  };
  const repriceSavedConfiguration = () => {
    if (!props.detail || !bundle || !beginAction('reprice')) {
      return;
    }
    void repriceConfiguration({
      configurationId: props.detail.configuration.id,
      repriceConfigurationRequestBody: {
        catalogueRevision: bundle.catalogueRevision,
        version: props.detail.configuration.version,
      },
    })
      .unwrap()
      .then(() => setRepriceConflict(false))
      .catch((error: unknown) => {
        if (isNewVehicleConflict(error)) {
          setRepriceConflict(true);
          void getConfigurationDetail(
            {configurationId: props.detail?.configuration.id ?? ''},
            false
          );
        } else if (isApiError(error)) {
          handleApiError(error);
        }
      })
      .finally(endAction);
  };
  const acceptConflict = () => {
    if (!conflict || !isConflictIntentCurrent || !beginAction('save')) {
      return;
    }
    void performSave(
      conflict.values,
      conflict.after.catalogueRevision,
      conflict.after,
      conflict.version
    ).finally(endAction);
  };

  const status = match({mode: props.mode, detail: props.detail})
    .with({mode: 'preview'}, () => ({
      label: getNewVehicleEnumLabel('modelYearStatus', props.previewContext?.status ?? 'draft', t),
      colorScheme: getNewVehicleEnumColorScheme(
        'modelYearStatus',
        props.previewContext?.status ?? 'draft'
      ),
      isSubtle: true,
    }))
    .with({detail: Pattern.nonNullable}, ({detail}) => ({
      label: getNewVehicleEnumLabel('priceValidity', detail.priceValidity, t),
      colorScheme: getNewVehicleEnumColorScheme('priceValidity', detail.priceValidity),
      isSubtle: true,
    }))
    .otherwise(() => undefined);
  const stepBlockers: Record<number, string | undefined> = {
    0: hasSelectableTrim
      ? undefined
      : t(
          'entity.newVehicle.hints.selectOrderableTrim',
          'Select an orderable trim level to continue'
        ),
    1: variantId
      ? undefined
      : t('entity.newVehicle.hints.selectEngine', 'Select an engine to continue'),
    3: match({
      colourMissing:
        Boolean(
          bundle?.colours.some(({availableForTrimIds}) => availableForTrimIds.includes(trimId))
        ) && !colourId,
      // A selected upholstery the chosen colour rules out (shown disabled) counts as missing too,
      // or it would pass here and only fail on Save.
      upholsteryMissing:
        Boolean(
          bundle?.upholsteries.some(({availableForTrimIds}) => availableForTrimIds.includes(trimId))
        ) &&
        (!upholsteryId ||
          Boolean(
            bundle?.colourUpholsteryRestrictions.some(
              (restriction) =>
                restriction.colourId === colourId && restriction.upholsteryId === upholsteryId
            )
          )),
    })
      .with({colourMissing: true, upholsteryMissing: true}, () =>
        t(
          'entity.newVehicle.hints.selectColourAndUpholstery',
          'Select a colour and upholstery to continue'
        )
      )
      .with({colourMissing: true}, () =>
        t('entity.newVehicle.hints.selectColour', 'Select a colour to continue')
      )
      .with({upholsteryMissing: true}, () =>
        t('entity.newVehicle.hints.selectUpholstery', 'Select upholstery to continue')
      )
      .otherwise(() =>
        hasIncompleteDiscountIntent
          ? t(
              'entity.newVehicle.notifications.completeProposedDiscount',
              'Complete all proposed discount fields'
            )
          : undefined
      ),
  };
  // Like the catalogue wizard, a step past the current one stays shut while an earlier step still
  // blocks, so the stepper cannot skip a required engine, colour or upholstery choice on the way
  // to Save. Preview and read-only viewers only look around and keep free navigation.
  const isBehindBlockedStep = (stepIndex: number) =>
    props.mode !== 'preview' &&
    props.canWrite &&
    stepIndex > currentStepIndex &&
    [1, 3].some(
      (blockedIndex) => blockedIndex < stepIndex && Boolean(getSafely(stepBlockers, blockedIndex))
    );
  const steps = [
    {title: t('entity.newVehicle.steps.modelTrim', 'Model / model year / trim level')},
    {
      title: t('entity.newVehicle.steps.engineDrive', 'Engine & drive'),
      isDisabled: !hasSelectableTrim,
    },
    {
      title: t('entity.newVehicle.steps.optionsPackages', 'Options & packages'),
      isDisabled: !hasSelectableTrim || isBehindBlockedStep(2),
    },
    {
      title: t('entity.newVehicle.steps.lookSummary', 'Colours & upholstery & summary'),
      isDisabled: !hasSelectableTrim || isBehindBlockedStep(3),
    },
    {
      title: t('entity.newVehicle.steps.reviewSave', 'Review & Save configuration'),
      isDisabled: !hasSelectableTrim || isBehindBlockedStep(4),
    },
  ];
  const blocker = stepBlockers[currentStepIndex];
  const reviewBlocker =
    currentTab === '5' && props.mode !== 'preview'
      ? [1, 3]
          .map((stepIndex) => ({stepIndex, message: stepBlockers[stepIndex]}))
          .find((item): item is {stepIndex: number; message: string} => Boolean(item.message))
      : undefined;
  // Steps keep showing the previous result while a new selection is evaluated, so auto-added
  // badges, locks and availability do not blink out (and shift the cards) on every click.
  // Anything that gates saving still waits for `currentData`.
  const displayedEvaluation =
    evaluation.isEligible && !evaluation.error ? evaluation.lastData : undefined;
  const evaluationRejectionMessage =
    evaluation.isEligible && isClientRejection(evaluation.error)
      ? [getApiErrorMessages(evaluation.error, '')].flat().join(' ').trim() || undefined
      : undefined;
  const exitAction = {
    type: 'button' as const,
    title: t('general.actions.exit', 'Exit'),
    variant: 'outlined' as const,
    onClick: () => navigate(vehiclesRoutes.newVehicleConfigurations),
  };
  const layoutProps = {
    basis,
    isBundleError,
    steps,
    currentStepIndex,
    onStepChange,
    bundle,
    evaluation: displayedEvaluation,
    isEvaluationEligible: evaluation.isEligible && !evaluation.error,
    isFetching: evaluation.isFetching,
    isSettled: evaluation.isSettled,
    canViewCost: props.canViewCost,
    trimId,
    variantId,
    colourId,
    upholsteryId,
    featureIds,
    packageIds,
    context: reviewContext,
    status,
    blocker,
    banner:
      props.mode === 'preview' ? (
        <Alert
          variant="info"
          message={
            props.previewContext?.status === 'active'
              ? t(
                  'entity.newVehicle.catalogue.publishedPreviewBanner',
                  'Catalogue preview \u2014 saving is disabled'
                )
              : t(
                  'entity.newVehicle.catalogue.draftPreviewBanner',
                  'Draft catalogue preview \u2014 saving is disabled'
                )
          }
        />
      ) : undefined,
  };

  return (
    <>
      <AdjustmentGuard
        key={`${evaluation.currentData?.catalogueRevision ?? ''}:${evaluation.currentData?.invalidatedSelectionIds.join(',') ?? ''}`}
        evaluation={evaluation.currentData}
        form={props.form}
        names={names}
      />
      <Show when={factoryFreezeReason}>
        <Alert variant="warning" message={factoryFreezeReason ?? ''} />
      </Show>
      <Show when={repriceConflict}>
        <Alert
          variant="warning"
          message={t(
            'entity.newVehicle.order.conflict',
            'This configuration changed. Review the refreshed details and try again.'
          )}
        />
      </Show>
      <Show when={isBundleError && currentTab !== '5'}>
        <Alert
          variant="error"
          message={t('general.labels.somethingWentWrong', 'Something went wrong.')}
        />
      </Show>
      <Show when={evaluation.isEligible && evaluation.error}>
        <VStack spacing={2} align="flex-start">
          <Alert
            variant={hasApiErrorCode(evaluation.error, 'DMS-NV-CF-0004') ? 'warning' : 'error'}
            message={
              hasApiErrorCode(evaluation.error, 'DMS-NV-CF-0004') && factoryFreezeMessage
                ? factoryFreezeMessage
                : (evaluationRejectionMessage ??
                  t(
                    'entity.newVehicle.notifications.priceUpdateFailed',
                    'The current price could not be loaded. Try again.'
                  ))
            }
          />
          {match({
            isRejection: Boolean(evaluationRejectionMessage),
            exceedsPrice: hasApiErrorCode(evaluation.error, 'DMS-NV-CF-0012'),
          })
            .with({exceedsPrice: true}, () => (
              <Button
                type="button"
                variant="secondary"
                title={steps[3].title}
                onClick={() => onStepChange(3)}
              />
            ))
            .with({isRejection: true}, () => null)
            .otherwise(() => (
              <Button
                type="button"
                variant="secondary"
                title={t('general.actions.tryAgain', 'Try again')}
                onClick={() => void evaluation.refetch()}
              />
            ))}
        </VStack>
      </Show>
      <Show when={reviewBlocker}>
        <Alert
          layout="detailed"
          variant="warning"
          message={reviewBlocker?.message ?? ''}
          actions={
            props.canWrite && reviewBlocker
              ? [
                  {
                    title: steps[reviewBlocker.stepIndex].title,
                    onClick: () => onStepChange(reviewBlocker.stepIndex),
                  },
                ]
              : undefined
          }
        />
      </Show>
      <Show
        when={
          currentTab === '5' &&
          Boolean(props.detail) &&
          Boolean(copyBlocker) &&
          copyBlocker !== reviewBlocker?.message
        }
      >
        <Alert variant="warning" message={copyBlocker ?? ''} />
      </Show>
      <Show when={currentTab === '5' && Boolean(copiedConfigurationId)}>
        <Alert
          layout="detailed"
          variant="success"
          message={t('general.notifications.success', 'Success')}
          actions={[
            {
              title: t('general.actions.open', 'Open'),
              onClick: () => setIsCopyOpenConfirmationOpen(true),
            },
          ]}
        />
      </Show>
      <ConfirmDialog
        isOpen={isCopyOpenConfirmationOpen}
        text={t(
          'entity.newVehicle.confirmations.discardChangesToOpenCopy',
          'Discard unsaved changes and open the copied configuration?'
        )}
        onClose={() => setIsCopyOpenConfirmationOpen(false)}
        onConfirm={openCopiedConfiguration}
      />
      <HStack justify="center" width="100%">
        <Box width="100%" maxWidth={NEW_VEHICLE_CONTENT_MAX_WIDTH}>
          <VStack spacing={5}>
            <NewVehiclePageHeader
              actions={[exitAction]}
              breadcrumbs={[
                {label: t('page.vehicles.title', 'Vehicles'), href: vehiclesRoutes.home},
                {
                  label: t('entity.newVehicle.configurations.title', 'New vehicle configurations'),
                  href: vehiclesRoutes.newVehicleConfigurations,
                },
                {label: t('entity.newVehicle.labels.configurator', 'Configurator')},
              ]}
              title={
                props.mode === 'preview'
                  ? `${t('entity.newVehicle.labels.preview', 'Preview')} · ${reviewContext.makeName} ${reviewContext.modelName} ${reviewContext.modelYearName}`
                  : (props.detail?.configuration.label ??
                    t('entity.newVehicle.actions.configure', 'Configure new vehicle'))
              }
              subtitle={t(
                'entity.newVehicle.labels.configuratorSubtitle',
                '5-step flow · save a vehicle configuration'
              )}
              parameters={
                props.detail
                  ? [
                      props.detail.currentPriceSnapshot.priceValidUntil
                        ? `${t('entity.newVehicle.labels.priceValidUntil', 'Price valid until')} ${formatDate(
                            'dateMedium',
                            parseDate(props.detail.currentPriceSnapshot.priceValidUntil)
                          )}`
                        : t('entity.newVehicle.labels.noExpiry', 'No expiry'),
                      `${t('entity.newVehicle.labels.catalogueRevision', 'Catalogue revision')} ${props.detail.currentPriceSnapshot.catalogueRevision}`,
                    ]
                  : undefined
              }
            />
            <Show when={currentTab === '1'}>
              <ConfiguratorLayout
                {...layoutProps}
                trailingActions={[
                  {
                    type: 'button',
                    title: t('entity.newVehicle.actions.compareTrims', 'Compare trim levels'),
                    variant: 'secondary',
                    leftIcon: 'action/compare_arrows',
                    isDisabled: (bundle?.trims.length ?? 0) < 2,
                    onClick: () => setCompareOpen(true),
                  },
                ]}
              >
                <DataStatus isLoading={props.isTreeLoading} isError={props.isTreeError}>
                  <ModelYearTrim
                    tree={props.tree}
                    bundle={bundle}
                    form={props.form}
                    control={props.control}
                    canSelect={props.canSelect}
                    isPreview={props.mode === 'preview'}
                    context={props.previewContext}
                    compareOpen={compareOpen}
                    onCompareClose={() => setCompareOpen(false)}
                    factoryFreezeReason={factoryFreezeReason}
                  />
                </DataStatus>
              </ConfiguratorLayout>
            </Show>
            <Show when={currentTab === '2'}>
              <ConfiguratorLayout {...layoutProps}>
                <EngineDrive
                  bundle={bundle}
                  trimId={trimId}
                  form={props.form}
                  evaluation={displayedEvaluation}
                  canSelect={props.canSelect}
                  factoryFreezeReason={factoryFreezeReason}
                />
              </ConfiguratorLayout>
            </Show>
            <Show when={currentTab === '3'}>
              <ConfiguratorLayout {...layoutProps}>
                <OptionsPackages
                  bundle={bundle}
                  evaluation={displayedEvaluation}
                  isEvaluationStale={Boolean(displayedEvaluation) && !evaluation.currentData}
                  form={props.form}
                  canSelect={props.canSelect}
                  factoryFreezeReason={factoryFreezeReason}
                />
              </ConfiguratorLayout>
            </Show>
            <Show when={currentTab === '4'}>
              <ConfiguratorLayout {...layoutProps}>
                <LookAndSummary
                  bundle={bundle}
                  trimId={trimId}
                  form={props.form}
                  control={props.control}
                  evaluation={displayedEvaluation}
                  isFetching={evaluation.isFetching}
                  isSettled={evaluation.isSettled || Boolean(evaluation.error)}
                  canViewCost={props.canViewCost}
                  canSelect={props.canSelect}
                  canWrite={props.canWrite}
                  factoryFreezeReason={factoryFreezeReason}
                />
              </ConfiguratorLayout>
            </Show>
            <Show when={currentTab === '5'}>
              <ConfiguratorLayout
                {...layoutProps}
                hideRail
                snapshot={priceSource === 'saved' ? props.detail?.currentPriceSnapshot : undefined}
                trailingActions={
                  props.mode === 'preview'
                    ? undefined
                    : [
                        ...(orderConfigurationId &&
                        props.onOrderConfiguration &&
                        isUnchangedSavedReview &&
                        !hasIncompleteDiscountIntent &&
                        !props.detail?.order &&
                        !props.detail?.vehicle
                          ? [
                              {
                                type: 'button' as const,
                                ...persistentOrderNowAction,
                                isDisabled:
                                  persistentOrderNowAction.isDisabled ||
                                  Boolean(pendingAction) ||
                                  isRecoveryPending,
                                onClick: () => {
                                  const intentKey = getDraftIntentKey(props.form.getValues());
                                  props.onOrderConfiguration?.(
                                    orderConfigurationId,
                                    () =>
                                      isMounted.current &&
                                      getDraftIntentKey(props.form.getValues()) === intentKey
                                  );
                                },
                              },
                            ]
                          : []),
                        ...(externalRegistrationConfigurationId && props.onRegisterExternalOrder
                          ? [
                              {
                                type: 'button' as const,
                                title:
                                  externalRegistrationBlocker ??
                                  t('entity.newVehicle.external.submit', 'Register external order'),
                                variant: 'outlined' as const,
                                isDisabled:
                                  Boolean(externalRegistrationBlocker) ||
                                  Boolean(pendingAction) ||
                                  isRecoveryPending,
                                onClick: () => {
                                  const intentKey = getDraftIntentKey(props.form.getValues());
                                  props.onRegisterExternalOrder?.(
                                    externalRegistrationConfigurationId,
                                    () =>
                                      isMounted.current &&
                                      getDraftIntentKey(props.form.getValues()) === intentKey
                                  );
                                },
                              },
                            ]
                          : []),
                        ...(props.canWrite && props.detail
                          ? [
                              {
                                type: 'button' as const,
                                title: t('entity.newVehicle.actions.saveCopy', 'Save as copy'),
                                variant: 'outlined' as const,
                                isDisabled:
                                  Boolean(copyBlocker) ||
                                  Boolean(pendingAction) ||
                                  isRecoveryPending,
                                isLoading: pendingAction === 'copy' || copyState.isLoading,
                                onClick: () => void copySavedConfiguration(),
                              },
                            ]
                          : []),
                        ...(props.canWrite
                          ? [
                              {
                                type: 'form-button' as const,
                                buttonType: 'submit' as const,
                                control: props.control,
                                title: t(
                                  'entity.newVehicle.actions.saveConfiguration',
                                  'Save configuration'
                                ),
                                leftIcon: 'navigation/check' as const,
                                isDisabled:
                                  !evaluation.isSettled ||
                                  Boolean(reviewBlocker) ||
                                  Boolean(pendingAction) ||
                                  isRecoveryPending,
                                isLoading:
                                  pendingAction === 'save' ||
                                  createState.isLoading ||
                                  updateState.isLoading,
                              },
                            ]
                          : []),
                      ]
                }
              >
                {isBundleError && priceSource === 'live' ? (
                  <Alert
                    variant="error"
                    message={t('general.labels.somethingWentWrong', 'Something went wrong.')}
                  />
                ) : (
                  <>
                    <Show when={saveGateStatus}>
                      <Text size="small" color="secondary" aria-live="polite">
                        {saveGateStatus}
                      </Text>
                    </Show>
                    <Review
                      isBundleError={isBundleError}
                      basis={basis}
                      control={props.control}
                      evaluation={evaluation.currentData}
                      detail={props.detail}
                      bundle={bundle}
                      context={reviewContext}
                      canViewCost={props.canViewCost}
                      canWrite={props.canWrite}
                      canEditCustomFields={props.canEditCustomFields}
                      priceSource={priceSource}
                      isSettled={
                        evaluation.isSettled || Boolean(evaluation.error) || Boolean(reviewBlocker)
                      }
                      isRepricing={pendingAction === 'reprice' || repriceState.isLoading}
                      isPreview={props.mode === 'preview'}
                      onStepChange={props.canWrite ? onStepChange : undefined}
                      onReprice={
                        props.canWrite && bundle
                          ? () => void repriceSavedConfiguration()
                          : undefined
                      }
                    />
                  </>
                )}
              </ConfiguratorLayout>
            </Show>
          </VStack>
        </Box>
      </HStack>
      <Dialog
        isOpen={Boolean(conflict)}
        title={t('entity.newVehicle.labels.reviewChanges', 'Review catalogue changes')}
        onClose={() => setConflict(undefined)}
        buttons={
          conflict
            ? [
                {
                  title: t('general.actions.cancel', 'Cancel'),
                  variant: 'secondary',
                  onClick: () => setConflict(undefined),
                },
                {
                  title: t('entity.newVehicle.actions.acceptSaveAgain', 'Accept and save again'),
                  isDisabled:
                    !isConflictIntentCurrent || Boolean(pendingAction) || isRecoveryPending,
                  isLoading:
                    pendingAction === 'save' || createState.isLoading || updateState.isLoading,
                  onClick: () => void acceptConflict(),
                },
              ]
            : []
        }
      >
        {conflict && isConflictIntentCurrent && bundle ? (
          <ConflictReviewContent
            basis={bundle.priceBasis}
            before={conflict.before}
            after={conflict.after}
            currency={bundle.currency}
            names={names}
            canViewCost={props.canViewCost}
          />
        ) : (
          <Text>
            {t(
              'entity.newVehicle.order.conflict',
              'This configuration changed. Review the refreshed details and try again.'
            )}
          </Text>
        )}
      </Dialog>
    </>
  );
});

// eslint-disable-next-line complexity -- Pre-existing violation
export function NewVehicleConfigurator({
  detail,
  draftKey,
  initialDraft,
  deepLink,
  businessCaseId,
  replaceSaleVehicleId,
  interestId,
  mode = 'configure',
  previewContext,
  onOrderConfiguration,
  onRegisterExternalOrder,
}: Props) {
  const navigate = useNavigate();
  const submitBridge = useRef<ConfiguratorSubmitBridge>(null);
  const treeQuery = useGetCatalogueTreeQuery(
    {},
    {
      skip: mode === 'preview',
    }
  );
  const {data: tree, isLoading: isTreeLoading, isError: isTreeError} = treeQuery;
  const deepModelYearId = !detail && !initialDraft ? deepLink?.modelYearId : undefined;
  const configuredDeepBundleQuery = useGetConfiguratorBundleQuery(
    {modelYearId: deepModelYearId ?? ''},
    {skip: !deepModelYearId || mode === 'preview'}
  );
  const previewDeepBundleQuery = useGetAuthorConfiguratorBundleQuery(
    {modelYearId: deepModelYearId ?? ''},
    {skip: !deepModelYearId || mode !== 'preview'}
  );
  const deepBundle =
    mode === 'preview' ? previewDeepBundleQuery.currentData : configuredDeepBundleQuery.currentData;
  const isDeepBundleLoading =
    mode === 'preview'
      ? previewDeepBundleQuery.isLoading || previewDeepBundleQuery.isFetching
      : configuredDeepBundleQuery.isLoading || configuredDeepBundleQuery.isFetching;
  const isDeepBundleError =
    mode === 'preview' ? previewDeepBundleQuery.isError : configuredDeepBundleQuery.isError;
  const costPermissionKey =
    mode === 'preview' ? 'newVehicleCatalogueViewCost' : 'newVehicleConfigurationViewCost';
  const [canViewCost, canEditCustomFields, canWrite, canOrder, canRegisterExternal] =
    usePermissions({
      permissionKeys: [
        costPermissionKey,
        'newVehicleConfigurationCustomFields',
        'newVehicleConfigurationWrite',
        'newVehicleConfigurationOrder',
        'newVehicleConfigurationRegisterExternal',
      ],
    });
  if (
    (mode !== 'preview' && isTreeLoading && !detail) ||
    (deepModelYearId && isDeepBundleLoading && !deepBundle)
  ) {
    return <Text>{t('general.labels.loading', 'Loading…')}</Text>;
  }
  if (
    (mode !== 'preview' && isTreeError && !detail) ||
    (deepModelYearId && isDeepBundleError && !deepBundle)
  ) {
    return (
      <VStack spacing={3} align="flex-start">
        <Alert
          variant="error"
          message={t('general.labels.somethingWentWrong', 'Something went wrong.')}
        />
        <HStack spacing={2}>
          <Button
            variant="secondary"
            title={t('entity.newVehicle.actions.backToCatalogue', 'Back to catalogue')}
            onClick={() => navigate(vehiclesRoutes.newVehicleCatalogue)}
          />
          <Button
            title={t('general.actions.tryAgain', 'Try again')}
            onClick={() => {
              if (mode !== 'preview' && isTreeError) {
                void treeQuery.refetch();
              }
              if (deepModelYearId && isDeepBundleError) {
                if (mode === 'preview') {
                  void previewDeepBundleQuery.refetch();
                } else {
                  void configuredDeepBundleQuery.refetch();
                }
              }
            }}
          />
        </HStack>
      </VStack>
    );
  }
  const initialSelectionContext = getSelectionContext(tree, deepBundle, {
    modelYearId: detail?.configuration.selection.modelYearId ?? deepModelYearId ?? '',
    trimId: detail?.configuration.selection.trimId ?? '',
    variantId: detail?.configuration.selection.variantId ?? '',
    colourId: detail?.configuration.selection.colourId,
    upholsteryId: detail?.configuration.selection.upholsteryId,
  });
  const deepTrim = deepBundle?.trims.find(({id}) => id === deepLink?.trimId);
  const acceptedDeepTrimId =
    deepTrim && (mode === 'preview' || getTrimOrderability(deepTrim, new Date()).orderable)
      ? deepTrim.id
      : '';
  const startValues: ConfiguratorFormValues = {
    makeId: initialSelectionContext.make?.id ?? previewContext?.makeId ?? '',
    modelId: initialSelectionContext.model?.id ?? previewContext?.modelId ?? '',
    modelYearId:
      deepModelYearId && (initialSelectionContext.model || previewContext) ? deepModelYearId : '',
    trimId: acceptedDeepTrimId,
    variantId: '',
    colourId: '',
    upholsteryId: '',
    featureIds: [],
    packageIds: [],
    label: '',
  };
  const defaults: ConfiguratorFormValues =
    initialDraft ??
    (detail
      ? {
          makeId: initialSelectionContext.make?.id ?? '',
          modelId: initialSelectionContext.model?.id ?? '',
          ...detail.configuration.selection,
          colourId: detail.configuration.selection.colourId ?? '',
          upholsteryId: detail.configuration.selection.upholsteryId ?? '',
          label: detail.configuration.label,
          proposedDiscount: detail.currentPriceSnapshot.proposedDiscount ?? undefined,
        }
      : startValues);
  return (
    <Form<ConfiguratorFormValues>
      shouldWatchForUnsavedChanges={mode !== 'preview'}
      defaultValues={defaults}
      experimentalZodSchema={configuratorFormSchema}
      onSubmit={async (values, _setErrors, _reset, blockerContext) => {
        if (submitBridge.current) {
          await submitBridge.current.submit(values, blockerContext);
        } else {
          blockerContext?.reportApiFailure?.();
        }
      }}
      onInvalidSubmit={(errors) => submitBridge.current?.invalidSubmit(errors)}
    >
      {(control, form, navigateWithoutBlocker) => (
        <ConfiguratorBody
          ref={submitBridge}
          control={control}
          form={form}
          tree={tree}
          isTreeLoading={isTreeLoading}
          isTreeError={isTreeError}
          canViewCost={Boolean(canViewCost)}
          canSelect={mode === 'preview' || Boolean(canWrite)}
          canWrite={Boolean(canWrite)}
          canOrder={Boolean(canOrder)}
          detail={detail}
          draftKey={draftKey}
          draftStartKey={getDraftContentKey(startValues)}
          businessCaseId={businessCaseId}
          replaceSaleVehicleId={replaceSaleVehicleId}
          interestId={interestId}
          mode={mode}
          canEditCustomFields={Boolean(canEditCustomFields)}
          navigateWithoutBlocker={navigateWithoutBlocker}
          previewContext={previewContext}
          onOrderConfiguration={onOrderConfiguration}
          onRegisterExternalOrder={canRegisterExternal ? onRegisterExternalOrder : undefined}
        />
      )}
    </Form>
  );
}
